import type { BondOrder } from '../domain/editor';
import { calloutTextBounds } from '../rendering/calloutGeometry';
import type { DrawingDocument } from '../model';
import { PaperBackgroundLayer } from './PaperShapeAnnotation';
import { PaperContextMenu } from './PaperContextMenu';
import { usePaperPastePosition } from './usePaperPastePosition';
import type { MarkushAttachmentLine } from '../domain/markushLayers';
import {
  repeatUnitAtLabel,
  repeatUnitBrackets,
  type RepeatUnitBrackets,
  type RepeatUnitRef
} from '../domain/markushRepeatBrackets';
import { findBondAtomIds, type BondAtomIds } from '../domain/objectEditing';
import { usePaperObjects } from './usePaperObjects';
import type { PaperShape } from '../domain/objectEditing';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { DrawingLine, NormalizedPoint, PageSetup, TextParagraph } from '../model';
import { RichTextEditor, type TextToolbarControls } from './RichTextEditor';
import { canvasTextMeasure } from './PaperTextAnnotation';
import { annotationExtentPoints } from '../rendering/annotationExtents';
import { DEFAULT_PAGE, gridSpacingOf } from '../domain/page';
import { contentBounds, extentOf, pageExtentFor, pageRect, withExtent } from '../domain/pageExtent';
import type { GridKind } from '../rendering/canvasGrid';
import type { SnapPoint } from '../domain/gridSnap';
import { useSnapToGrid } from './useSnapToGrid';
import {
  collectGuideSources,
  GUIDE_SNAP_DISTANCE_PX,
  resolveSnap,
  type GuideSources,
  type SnapResult
} from '../domain/snapGuides';
import type { BondStereoValue } from '@peruselab/structure-editor/domain/documentSelectors';
import type { DrawingAtom } from '@peruselab/structure-editor/domain/graphDrawing';
import {
  hasSelection,
  type ArmedBondTool,
  type EditorSelection,
  type EditorTool
} from '@peruselab/structure-editor/domain/editor';
import {
  DEFAULT_BOND_DRAWING_CONFIG,
  distanceBetween,
  normalizedToPixel,
  proposedBondEndpoints,
  type BondDrawingConfig,
  type PixelPoint
} from '@peruselab/structure-editor/domain/bonds';
import { roundToOneDecimal, type PaperSize } from '@peruselab/structure-editor/domain/geometry';
import {
  createRingTemplatePlacement,
  cycleTemplate,
  type RingTemplate
} from '@peruselab/structure-editor/domain/ringTemplates';
import {
  MOUSE_SELECTION_DISTANCE_PX,
  TOUCH_SELECTION_DISTANCE_PX,
  nearestPoint,
  selectionAtPoint,
  uniqueEndpoints,
  usesTouchTarget
} from '@peruselab/structure-editor/domain/paperHitTesting';
import {
  centerPageAxes,
  panViewportBy,
  screenToNormalized,
  wheelZoomFactor
} from '@peruselab/structure-editor/domain/viewport';
import { PaperViewportControls } from './PaperViewportControls';
import { PaperVectorLayer, type PaperReactionZone, type ReactionZoneRefs } from './PaperVectorLayer';
import {
  translateRenderable,
  type RenderableAnnotation,
  type PaperArrow
} from '@peruselab/structure-editor/domain/annotationRendering';
import type { ProposalMarker, SvgHaptoLabel } from './drawingPaperRenderModel';
import { usePaperRenderers } from './usePaperRenderers';
import { usePaperBlinkMarkers } from './usePaperBlinkMarkers';
import { usePaperViewport } from './usePaperViewport';
import { usePaperPointer } from './usePaperPointer';
import { usePaperArrows } from './usePaperArrows';
import { usePaperTouchNavigation } from './usePaperTouchNavigation';
import { usePaperGeometry } from './usePaperGeometry';
import { isPrimaryModifier } from './escapeArbiter';
import type { ViewControlGroup } from './PaperViewportControls';
import { centroidOf } from '../domain/haptoCentroid';
import type { IonicPair } from '../domain/ionicPairs';

const UNBOUNDED_EDITING = { minX: -1e6, minY: -1e6, maxX: 1e6, maxY: 1e6 } as const;

// #762 -- per-device preferences for Pen-only mode.
const PEN_SEEN_KEY = 'peruselab.penSeen';
const PEN_ONLY_KEY = 'peruselab.penOnly';
function readDeviceFlag(key: string): boolean {
  try {
    return globalThis.localStorage?.getItem(key) === 'true';
  } catch {
    return false;
  }
}
function writeDeviceFlag(key: string, value: boolean | string): void {
  try {
    globalThis.localStorage?.setItem(key, String(value));
  } catch {
    // Storage unavailable (private window): the setting lasts for this visit only.
  }
}

// #772 part 2 -- per-device grid view preferences: shown or hidden (hidden by default) and line or
// dot. The spacing belongs to the drawing (page.gridSpacing).
const SHOW_GRID_KEY = 'peruselab.showGrid';
const GRID_KIND_KEY = 'peruselab.gridKind';
// #772 part 2b -- Snap to grid, per device, off by default; independent of showing the grid.
// #772 part 3 -- smart alignment guides, per device, on by default (stored only once turned off).
const SMART_GUIDES_KEY = 'peruselab.smartGuides';
function readDeviceFlagDefaultOn(key: string): boolean {
  try {
    return globalThis.localStorage?.getItem(key) !== 'false';
  } catch {
    return true;
  }
}
const toUnits = (point: NormalizedPoint, size: PaperSize) => ({ x: point.x * size.width, y: point.y * size.height });
function readGridKind(): GridKind {
  try {
    return globalThis.localStorage?.getItem(GRID_KIND_KEY) === 'dot' ? 'dot' : 'line';
  } catch {
    return 'line';
  }
}

/** A client (viewport) pixel point to a paper-normalized point, through the current pan and zoom. */
export type ClientToPaperPoint = (clientX: number, clientY: number) => NormalizedPoint | null;

interface DrawingPaperProps {
  readonly onPasteAt?: (point: NormalizedPoint) => void;
  readonly canPaste?: boolean;
  readonly onPointerPositionChange?: (point: NormalizedPoint) => void;
  readonly lines: readonly DrawingLine[];
  readonly atoms: readonly DrawingAtom[];
  readonly bondOrders: Readonly<Record<string, BondOrder>>;
  readonly armedBondTool?: ArmedBondTool;
  readonly armedBondOrder: BondOrder;
  readonly aromaticBondIds: ReadonlySet<string>;
  readonly aromaticRingCenters: Readonly<Record<string, NormalizedPoint>>;
  readonly annotations: readonly RenderableAnnotation[];
  readonly stereoBonds: Readonly<Record<string, BondStereoValue>>;
  /** #868 -- per-atom / per-bond appearance (molecule styling, whole or segment). */
  readonly atomAppearance?: DrawingDocument['depiction']['atomAppearance'];
  readonly bondAppearance?: DrawingDocument['depiction']['bondAppearance'];
  readonly cycleFills?: DrawingDocument['depiction']['cycleFills'];
  readonly tool: EditorTool;
  readonly selection: EditorSelection;
  readonly invalidAtomIds: ReadonlySet<string>;
  readonly invalidBondIds: ReadonlySet<string>;
  // ADR 0020 D3 (#1099) -- the server check's warnings (amber), and each marked atom's reason,
  // shown as the paper's tooltip while the pointer is on that atom.
  readonly warningAtomIds?: ReadonlySet<string>;
  readonly warningBondIds?: ReadonlySet<string>;
  readonly markReasons?: ReadonlyMap<string, string>;
  readonly onSelectionChange: (selection: EditorSelection) => void;
  readonly onAtomChargeApply?: ((atomId: string) => void) | undefined;
  readonly onAtomElementChange: (atomId: string, atomicNumber: number) => void;
  readonly onAtomPlace: (point: NormalizedPoint, atomicNumber: number) => void;
  // #648 -- Markush mode's R-group label tool; see usePaperPointer.
  readonly onAtomRGroupLabel?: (atomId: string, label: string) => void;
  readonly onPaperRGroupLabel?: (point: NormalizedPoint, label: string) => void;
  /** #736 -- a functional group (its key) on a clicked atom, or alone on empty paper. */
  readonly onAtomGroup?: (atomId: string, key: string) => void;
  readonly onPaperGroup?: (point: NormalizedPoint, key: string) => void;
  // #654 -- Markush mode's atom-list tool; see usePaperPointer.
  readonly onAtomAtomList?: (atomId: string, allowedAtomicNumbers: readonly number[]) => void;
  // #650 -- Markush attachment-point lines (label -> ring centre), paper-normalized like `atoms`.
  readonly markushAttachmentLines?: readonly MarkushAttachmentLine[];
  // ADR 0014 P1 -- the reaction zones around the drawing's arrow (paper pixels, labelled).
  readonly reactionZones?: readonly PaperReactionZone[];
  /** #700 -- each reaction box's element (a drop target for the app's drag-and-drop). */
  readonly reactionZoneRefs?: ReactionZoneRefs;
  /** A reaction box was dragged: move it (and its molecules) by this paper-normalized offset. */
  readonly onReactionBoxMove?: (zone: PaperReactionZone['zone'], offset: NormalizedPoint) => void;
  /** #699 -- receives a converter from a client (viewport) point to a paper-normalized point, for
   *  the workspace's drop target: where something dragged onto the paper lands in the drawing. */
  readonly clientPointRef?: Ref<ClientToPaperPoint>;
  // #656 -- Markush repeat units to bracket, and what a press on one's count label does (opens the
  // count editor at that client point).
  readonly markushRepeatUnits?: readonly RepeatUnitRef[];
  readonly onRepeatUnitActivate?: (unitId: string, clientPoint: Readonly<{ x: number; y: number }>) => void;
  readonly onBondToolApply: (bondId: string) => void;
  readonly onImplicitHydrogenDirectionChange: (atomId: string, direction: Readonly<{ x: number; y: number }>) => void;
  readonly onAtomsMove: (positions: Readonly<Record<string, NormalizedPoint>>) => void;
  readonly onLinesChange: (lines: readonly DrawingLine[]) => void;
  readonly onRingPlace: (lines: readonly DrawingLine[], template: RingTemplate, sharedBondId?: string) => void;
  readonly onChainPlace: (lines: readonly DrawingLine[]) => void;
  readonly onShapePlace: (
    shape: 'ellipse' | 'rect' | 'triangle' | 'star',
    start: NormalizedPoint,
    end: NormalizedPoint
  ) => void;
  readonly onShapeChange: (shape: PaperShape) => void;
  readonly onArrowChange: (arrow: PaperArrow) => void;
  /** #756 -- one committed typing burst or formatting step of a standalone text annotation, as one
   *  history step. `position` (paper-normalized top-left) matters only when `id` is new; empty
   *  `paragraphs` remove the annotation. Without it the text tool is inert. */
  readonly onTextCommit?: (id: string, position: NormalizedPoint, paragraphs: readonly TextParagraph[]) => void;
  /** #1137 follow-up -- editing the text `id` ended (its last burst already committed). */
  readonly onTextEditEnd?: (id: string) => void;
  /** #756/#637 -- selected annotations dragged by a text block in Select mode: the paper-normalized
   *  offset to move every selected annotation by (one history step). */
  readonly onAnnotationsMove?: (offset: NormalizedPoint) => void;
  /** #772 -- the drawing's page, drawn as a sheet (sizes in drawing units = surface px at 100%). */
  readonly page?: PageSetup;
  /** #772 -- page setup changes (one undoable step each); absent hides the Page setup control. */
  readonly onPageChange?: (page: PageSetup) => void;
  /** Undo/Redo pressed inside the text editor (it commits its pending burst first). */
  readonly onUndo?: () => void;
  readonly onRedo?: () => void;
  /** The host's formatting toolbar for the text editor (icons and labels live in the host). */
  readonly renderTextToolbar?: (controls: TextToolbarControls) => ReactNode;
  readonly onWebGlAvailabilityChange: (available: boolean) => void;
  readonly onPaperSizeChange?: (size: PaperSize) => void;
  readonly bondConfig?: BondDrawingConfig;
  readonly bondAtomIds?: BondAtomIds;
  /** ADR 0026: each hapto bond's ligand atoms, so its line follows their middle while they move. */
  readonly haptoLigands?: Readonly<Record<string, readonly string[]>>;
  /** ADR 0026 D6: each agostic interaction's hydrogen and carbon, so its start follows their middle. */
  readonly agosticStarts?: Readonly<Record<string, readonly [string, string]>>;
  /** ADR 0026 D2 -- "Show hapticity": an η label beside each hapto bond (a view option). */
  readonly showHapticity?: boolean;
  readonly documentId?: string;
  readonly isOversizedImport?: boolean;
  // #508 — when supplied, PaperViewportControls portals into this node (e.g. a slot in the
  // host app's toolbar row) instead of rendering as its default absolutely-positioned overlay
  // on the canvas. Keeps this package owning the controls' markup/behavior while letting the
  // host decide where they actually sit in the page.
  readonly viewportControlsContainer?: HTMLElement | null;
  /** #807 R4 -- view-control groups the host toolbar has no room for, and where its ⋯ menu offers
   *  them (see PaperViewportControls). */
  readonly viewHiddenGroups?: ReadonlySet<ViewControlGroup>;
  readonly viewOverflowContainer?: HTMLElement | null;
  readonly viewOverflowAnchor?: HTMLElement | null;
  readonly onViewOverflowClose?: () => void;
  // #510 — an extra, host-supplied line shown first in the empty-canvas hint overlay (below).
  // Lets the app fold its search-mode-specific idle copy ("Draw a structure to search for
  // matches.") into this same in-canvas overlay instead of a separate flow row elsewhere on the
  // page, since they're both gated on the same "nothing drawn yet" condition and only one of them
  // needs to own it.
  readonly idleHint?: string | undefined;
  // #575 -- Edit tab "Show Node Numbers" toggle, a debug overlay. Off by default.
  readonly showNodeNumbers?: boolean;
  // #1047 -- Arrange tab "Show R/S Labels": each stereocentre's "R" / "S" by atom id. Absent when off.
  readonly stereoLabels?: Readonly<Record<string, string>> | undefined;
  /** #780 -- "E" / "Z" by bond id, beside each defined double bond, with the R/S labels. */
  readonly bondStereoLabels?: Readonly<Record<string, string>> | undefined;
  // #576 -- Edit tab "Show Bond Acceptors" toggle, a drawing affordance overlay. Off by default.
  readonly showBondAcceptors?: boolean;
  // #577 -- Edit tab "Center of Rotation" toggle. When on and at least 2 atoms are part of the
  // selection (selectedTransformAtomIds, the same set transformSelection's rotate/flip operate
  // on), the paper shows a crosshair at the pivot and clicking anywhere places it there instead of
  // running the usual select/drag gesture.
  readonly showCenterOfRotation?: boolean;
  readonly selectedTransformAtomIds?: readonly string[];
  readonly rotationPivot?: NormalizedPoint | null;
  readonly onRotationPivotChange?: (point: NormalizedPoint) => void;
  // #578 -- Edit tab "Lasso Selection" toggle. While true and `tool === 'select'`, a drag starting
  // on empty paper draws a freehand path instead of the rectangular marquee.
  readonly lassoSelectionEnabled?: boolean;
  // ADR 0022 D2 -- the drawing's "Show ionic bonds" Style option: every bolt shows, not only the
  // hovered or selected ones.
  readonly showIonicBonds?: boolean;
  // ADR 0022 (as amended) -- the salt's cation-anion pairs, drawn as bolts (`ionicPairs`).
  readonly ionicPairs?: readonly IonicPair[];
}

const NO_HAPTO_LIGANDS: Readonly<Record<string, readonly string[]>> = {};
const NO_AGOSTIC_STARTS: Readonly<Record<string, readonly [string, string]>> = {};
const SUPERSCRIPT_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const superscript = (count: number) => [...String(count)].map((digit) => SUPERSCRIPT_DIGITS[Number(digit)]).join('');
const NO_IONIC_PAIRS: readonly IonicPair[] = [];

export function DrawingPaper({
  onPasteAt,
  canPaste = false,
  onPointerPositionChange,
  lines,
  atoms,
  bondOrders,
  armedBondOrder,
  armedBondTool = { kind: 'order', order: armedBondOrder },
  aromaticBondIds,
  aromaticRingCenters,
  annotations,
  stereoBonds,
  atomAppearance,
  bondAppearance,
  cycleFills,
  tool,
  selection,
  invalidAtomIds,
  invalidBondIds,
  warningAtomIds,
  warningBondIds,
  markReasons,
  onSelectionChange,
  onAtomElementChange,
  onAtomChargeApply,
  onAtomPlace,
  onAtomRGroupLabel,
  onPaperRGroupLabel,
  onAtomGroup,
  onPaperGroup,
  onAtomAtomList,
  markushAttachmentLines,
  reactionZones,
  reactionZoneRefs,
  onReactionBoxMove,
  clientPointRef,
  markushRepeatUnits,
  onRepeatUnitActivate,
  onBondToolApply,
  onImplicitHydrogenDirectionChange,
  onAtomsMove,
  onLinesChange,
  onRingPlace,
  onChainPlace,
  onShapePlace,
  onArrowChange,
  onShapeChange,
  onTextCommit,
  onTextEditEnd,
  page = DEFAULT_PAGE,
  onPageChange,
  onAnnotationsMove,
  onUndo,
  onRedo,
  renderTextToolbar,
  onWebGlAvailabilityChange,
  onPaperSizeChange,
  bondConfig = DEFAULT_BOND_DRAWING_CONFIG,
  documentId,
  bondAtomIds,
  haptoLigands = NO_HAPTO_LIGANDS,
  agosticStarts = NO_AGOSTIC_STARTS,
  showHapticity = false,
  isOversizedImport = false,
  viewportControlsContainer = null,
  viewHiddenGroups,
  viewOverflowContainer = null,
  viewOverflowAnchor = null,
  onViewOverflowClose,
  idleHint,
  showNodeNumbers = false,
  stereoLabels,
  bondStereoLabels,
  showBondAcceptors = false,
  showCenterOfRotation = false,
  selectedTransformAtomIds = [],
  rotationPivot = null,
  onRotationPivotChange,
  lassoSelectionEnabled = false,
  showIonicBonds = false,
  ionicPairs = NO_IONIC_PAIRS
}: DrawingPaperProps) {
  const { t } = useTranslation();
  const paperRef = useRef<HTMLDivElement | null>(null);
  // #756 -- the text annotation being edited inline (`position` only matters for a new one).
  const [textEdit, setTextEdit] = useState<{
    readonly id: string;
    readonly position: NormalizedPoint;
    readonly initialInput?: string;
  } | null>(null);
  const textEditRef = useRef(textEdit);
  useEffect(() => {
    textEditRef.current = textEdit;
  }, [textEdit]);
  // The press that closed the editor (by time stamp) must not also start a new edit or gesture.
  const textClosingPressRef = useRef<number | null>(null);
  const endTextEdit = useCallback(
    (closingPointerTimeStamp?: number) => {
      textClosingPressRef.current = closingPointerTimeStamp ?? null;
      const ended = textEditRef.current?.id;
      setTextEdit(null);
      if (ended) onTextEditEnd?.(ended);
    },
    [onTextEditEnd]
  );
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; point: NormalizedPoint } | null>(null);
  const closeContextMenu = useCallback((restoreFocus = true) => {
    setContextMenu(null);
    if (restoreFocus) paperRef.current?.focus({ preventScroll: true });
  }, []);
  const gridCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const webGlCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Shared with both usePaperRenderers (which writes it from its ResizeObserver) and
  // usePaperViewport (which reads it for zoomBy/fitStructure) -- created here, not inside
  // either hook, since each hook would otherwise need the other's output before it could run.
  const sizeRef = useRef<PaperSize>({ width: 1, height: 1 });
  const [showGrid, setShowGrid] = useState(() => readDeviceFlag(SHOW_GRID_KEY));
  const [gridKind, setGridKind] = useState<GridKind>(readGridKind);
  const toggleGrid = useCallback(() => {
    setShowGrid((current) => {
      writeDeviceFlag(SHOW_GRID_KEY, !current);
      return !current;
    });
  }, []);
  const changeGridKind = useCallback((kind: GridKind) => {
    writeDeviceFlag(GRID_KIND_KEY, kind);
    setGridKind(kind);
  }, []);
  const gridSpacing = gridSpacingOf(page);
  const [snapGrid, setSnapGrid] = useSnapToGrid();
  const toggleSnapGrid = useCallback(() => setSnapGrid(!snapGrid), [setSnapGrid, snapGrid]);
  // #772 part 3 -- smart alignment guides: on by default, per device.
  const [smartGuides, setSmartGuides] = useState(() => readDeviceFlagDefaultOn(SMART_GUIDES_KEY));
  const toggleSmartGuides = useCallback(() => {
    setSmartGuides((current) => {
      writeDeviceFlag(SMART_GUIDES_KEY, !current);
      return !current;
    });
  }, []);
  // The touch "place freely" control: held, it bypasses snapping like Alt/Option.
  const bypassRef = useRef(false);
  const [bypassHeld, setBypassHeld] = useState(false);
  const holdBypass = useCallback((held: boolean) => {
    bypassRef.current = held;
    setBypassHeld(held);
  }, []);
  // One stable function for every pointer hook; it reads the live settings through a ref, which
  // an effect below keeps current (guide sources need the paper size from usePaperRenderers).
  const snapSettingsRef = useRef<{
    grid: boolean;
    spacing: number;
    guides: boolean;
    guideSources: GuideSources | null;
    zoom: number;
  }>({ grid: snapGrid, spacing: gridSpacing, guides: smartGuides, guideSources: null, zoom: 1 });
  // The active snap target, shown while a gesture is snapping (guide lines and a target marker).
  const [snapFeedback, setSnapFeedback] = useState<SnapResult | null>(null);
  const snapFeedbackRef = useRef<SnapResult | null>(null);
  const showSnapFeedback = useCallback((next: SnapResult | null) => {
    if (JSON.stringify(snapFeedbackRef.current) === JSON.stringify(next)) return;
    snapFeedbackRef.current = next;
    setSnapFeedback(next);
  }, []);
  const snapPoint = useCallback<SnapPoint>(
    (point, modifiers) => {
      const { grid: gridOn, spacing, guides, guideSources, zoom } = snapSettingsRef.current;
      const size = sizeRef.current;
      if (modifiers?.altKey || bypassRef.current || (!gridOn && !guides) || !(size.width > 0)) {
        if (!modifiers?.quiet) showSnapFeedback(null);
        return point;
      }
      const result = resolveSnap(toUnits(point, size), {
        guides: guides ? guideSources : null,
        gridSpacing: gridOn ? spacing : null,
        maxDistance: GUIDE_SNAP_DISTANCE_PX / Math.max(zoom, 1e-6),
        ...(modifiers?.exclude ? { exclude: modifiers.exclude } : {})
      });
      if (!modifiers?.quiet) showSnapFeedback(result.snapped ? result : null);
      return { x: result.x / size.width, y: result.y / size.height };
    },
    [showSnapFeedback]
  );
  const grid = useMemo(
    () => (showGrid ? { kind: gridKind, spacing: gridSpacing } : null),
    [showGrid, gridKind, gridSpacing]
  );
  // #807 -- the zoom a Fit command chose: while the view is still at exactly that zoom, the zoom
  // menu reads "Fit" (as Google Docs does); any other zoom shows the percentage again.
  const [fitZoom, setFitZoom] = useState<number | null>(null);
  const markFit = (zoom: number | null) => setFitZoom(zoom);
  const {
    viewport,
    setViewport,
    setLayoutViewport,
    autoCenterPageRef,
    panDrag,
    zoomBy,
    fitPoints,
    actualSize,
    beginPan,
    updatePan,
    endPan
  } = usePaperViewport({
    atoms,
    sizeRef,
    documentId,
    isOversizedImport
  });
  const { linesRef, viewportRef, paperSize } = usePaperRenderers({
    paperRef,
    gridCanvasRef,
    webGlCanvasRef,
    sizeRef,
    lines,
    viewport,
    grid,
    onWebGlAvailabilityChange,
    onPaperSizeChange
  });
  // #848 -- resize around the page at the current zoom, including fitted/oversized imports.
  // A deliberate pan keeps its position until Fit, Actual size or a new document resets it.
  const pageBounds = useMemo(() => pageRect(page), [page]);
  useEffect(() => {
    if (!autoCenterPageRef.current || paperSize.width <= 1 || paperSize.height <= 1) return;
    setLayoutViewport((current) => centerPageAxes(current, paperSize, pageBounds));
  }, [documentId, paperSize, pageBounds, setLayoutViewport, autoCenterPageRef]);
  const trackPastePosition = usePaperPastePosition(viewport, paperSize, onPointerPositionChange);
  // #772 part 3 -- guide sources in drawing units, rebuilt only when the drawing, page or paper
  // size changes; the snap function reads them (and the zoom) through snapSettingsRef.
  const guideSources = useMemo(
    () =>
      collectGuideSources(
        pageRect(page),
        atoms.map((atom) => ({ id: atom.id, position: toUnits(atom.position, paperSize) })),
        annotations.map((annotation) => ({
          id: annotation.id,
          extent: annotationExtentPoints(annotation, paperSize, canvasTextMeasure).map((point) =>
            toUnits(point, paperSize)
          )
        }))
      ),
    [annotations, atoms, page, paperSize]
  );
  useEffect(() => {
    snapSettingsRef.current = {
      grid: snapGrid,
      spacing: gridSpacing,
      guides: smartGuides,
      guideSources,
      zoom: viewport.zoom
    };
  }, [snapGrid, gridSpacing, smartGuides, guideSources, viewport.zoom]);
  const { blinkMarkers, addBlinkMarker } = usePaperBlinkMarkers(sizeRef);

  const ringTemplate: RingTemplate | null =
    tool === 'benzene'
      ? { size: 6, aromatic: true }
      : tool.startsWith('cycle-')
        ? cycleTemplate(tool.slice(6))
        : tool.startsWith('ring-')
          ? { size: Number(tool.slice(5)), aromatic: false }
          : null;

  // #656 -- the brackets are computed below from the *rendered* atoms (they follow a drag), while
  // the pointer hook is set up first; it reads the latest brackets through this ref at press time.
  const repeatBracketsRef = useRef<readonly RepeatUnitBrackets[]>([]);
  const repeatUnitLabelAt = useCallback((point: PixelPoint) => repeatUnitAtLabel(repeatBracketsRef.current, point), []);
  // ADR 0014 -- atom id -> the whole molecule, for molecules sitting in a reaction box.
  const boxedMolecule = useMemo(() => {
    const byAtom = new Map<string, readonly string[]>();
    for (const zone of reactionZones ?? [])
      for (const molecule of zone.molecules) for (const id of molecule) byAtom.set(id, molecule);
    return byAtom.size ? (atomId: string) => byAtom.get(atomId) : undefined;
  }, [reactionZones]);

  const {
    activeDrafts,
    hoveredEndpoint,
    hoveredIonicBondId,
    hoveredAtomId,
    hydrogenPreviewDirections,
    atomDrag,
    ringPointer,
    shapeDrag,
    marqueeDrag,
    lassoDrag,
    handlePointerDown,
    handlePointerMove,
    finishPointer,
    handlePointerLeave,
    cancel: cancelPointerGesture
  } = usePaperPointer({
    snapPoint,
    tool,
    // #637 -- box/lasso enclose annotations too, with text measured as the paper draws it.
    annotations,
    measureText: canvasTextMeasure,
    lassoModeActive: lassoSelectionEnabled,
    ...(reactionZones?.length ? { reactionZones } : {}),
    ...(onReactionBoxMove ? { onReactionBoxMove } : {}),
    ...(boxedMolecule ? { boxedMolecule } : {}),
    ...(documentId ? { documentId } : {}),
    atoms,
    lines,
    bondOrders,
    ionicPairs,
    armedBondOrder,
    bondConfig,
    ringTemplate,
    selection,
    onSelectionChange,
    onAtomElementChange,
    onAtomChargeApply,
    onAtomPlace,
    ...(onAtomRGroupLabel ? { onAtomRGroupLabel } : {}),
    ...(onPaperRGroupLabel ? { onPaperRGroupLabel } : {}),
    ...(onAtomGroup ? { onAtomGroup } : {}),
    ...(onPaperGroup ? { onPaperGroup } : {}),
    ...(onAtomAtomList ? { onAtomAtomList } : {}),
    ...(onRepeatUnitActivate ? { repeatUnitLabelAt, onRepeatUnitActivate } : {}),
    onBondToolApply,
    onImplicitHydrogenDirectionChange,
    onAtomsMove,
    onLinesChange,
    onRingPlace,
    onShapePlace,
    sizeRef,
    linesRef,
    viewportRef,
    addBlinkMarker,
    panDrag,
    beginPan,
    updatePan,
    endPan
  });

  const graphEndpoints = useMemo(() => bondAtomIds ?? findBondAtomIds(atoms, lines), [bondAtomIds, atoms, lines]);
  const objectGestures = usePaperObjects({
    snapPoint,
    ...(boxedMolecule ? { boxedMolecule } : {}),
    tool,
    atoms,
    lines,
    annotations,
    selection,
    onSelectionChange,
    onAtomsMove,
    ...(onAnnotationsMove ? { onAnnotationsMove } : {}),
    onShapeChange,
    onRingPlace,
    onChainPlace,
    ringTemplate,
    paperSize,
    zoom: viewport.zoom,
    bondAtomIds: graphEndpoints,
    bondOrders,
    // #772: objects may extend beyond the page (accepted overflow), so placement and editing are
    // not bounded by it or by the visible paper.
    editingBounds: UNBOUNDED_EDITING,
    bondLength: bondConfig.lengthPx,
    sizeRef,
    viewportRef,
    ...(documentId ? { documentId } : {})
  });
  const previewPositions = objectGestures.positions ?? atomDrag?.currentPositions;

  const renderedAtoms = useMemo(
    () =>
      previewPositions
        ? atoms.map((atom) => (previewPositions[atom.id] ? { ...atom, position: previewPositions[atom.id]! } : atom))
        : atoms,
    [previewPositions, atoms]
  );

  const repeatBrackets = useMemo(() => {
    if (!markushRepeatUnits?.length) return [];
    const positions = new Map(renderedAtoms.map((atom) => [atom.id, normalizedToPixel(atom.position, paperSize)]));
    const edges = Object.values(graphEndpoints).map(([source, target]) => ({ source, target }));
    return markushRepeatUnits.flatMap((unit) => {
      const brackets = repeatUnitBrackets(positions, edges, unit);
      return brackets ? [brackets] : [];
    });
  }, [graphEndpoints, markushRepeatUnits, paperSize, renderedAtoms]);
  useEffect(() => {
    repeatBracketsRef.current = repeatBrackets;
  }, [repeatBrackets]);

  const renderedLines = useMemo(() => {
    if (!previewPositions) return lines;
    const atomPositions = new Map(atoms.map((atom) => [atom.id, atom.position]));
    const positionOf = (id: string) => previewPositions[id] ?? atomPositions.get(id);
    return lines.map((line) => {
      const ids = graphEndpoints[line.id];
      const ligand = haptoLigands[line.id];
      const agostic = agosticStarts[line.id];
      if (agostic) {
        return {
          ...line,
          start: centroidOf(agostic, positionOf) ?? line.start,
          end: (ids && previewPositions[ids[1]]) ?? line.end
        };
      }
      if (ligand) {
        return {
          ...line,
          start: (ids && previewPositions[ids[0]]) ?? line.start,
          end: centroidOf(ligand, positionOf) ?? line.end
        };
      }
      if (!ids) return line;
      return { ...line, start: previewPositions[ids[0]] ?? line.start, end: previewPositions[ids[1]] ?? line.end };
    });
  }, [previewPositions, graphEndpoints, haptoLigands, agosticStarts, lines, atoms]);

  // ADR 0026 D2 -- each hapto bond's η label, beside the middle of its line, on the side away
  // from the paper's "up" so it reads like the bond's own caption.
  const haptoLabels = useMemo<readonly SvgHaptoLabel[]>(() => {
    if (!showHapticity) return [];
    return renderedLines.flatMap((line) => {
      const ligand = haptoLigands[line.id];
      if (!ligand) return [];
      const start = normalizedToPixel(line.start, paperSize);
      const end = normalizedToPixel(line.end, paperSize);
      const length = Math.hypot(end.x - start.x, end.y - start.y) || 1;
      const offset = 10;
      const nx = -(end.y - start.y) / length;
      const ny = (end.x - start.x) / length;
      return [
        {
          id: line.id,
          x: (start.x + end.x) / 2 + nx * offset,
          y: (start.y + end.y) / 2 + ny * offset,
          text: `η${superscript(ligand.length)}`
        }
      ];
    });
  }, [haptoLigands, paperSize, renderedLines, showHapticity]);

  const previewRingCenters = useMemo(() => {
    if (!previewPositions) return aromaticRingCenters;
    const centers = { ...aromaticRingCenters };
    const byId = new Map(renderedAtoms.map((a) => [a.id, a.position]));
    for (const ring of objectGestures.rings) {
      const points = ring.atomIds.map((id) => byId.get(id)!);
      const center = {
        x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
        y: points.reduce((sum, p) => sum + p.y, 0) / points.length
      };
      for (const id of ring.bondIds) if (centers[id]) centers[id] = center;
    }
    return centers;
  }, [previewPositions, aromaticRingCenters, objectGestures.rings, renderedAtoms]);

  // ADR 0022 (as amended) -- which bolts between a salt's ions show: all with "Show ionic bonds";
  // otherwise the hovered one and those touching a selected ion.
  const shownIonicIds = useMemo(
    () =>
      new Set(
        ionicPairs
          .filter(
            (pair) =>
              showIonicBonds ||
              pair.id === hoveredIonicBondId ||
              selection.atomIds.includes(pair.cationId) ||
              selection.atomIds.includes(pair.anionId)
          )
          .map((pair) => pair.id)
      ),
    [hoveredIonicBondId, ionicPairs, selection, showIonicBonds]
  );
  const { labelScale, bondStrokeWidth, svgLines, stereoWedges, svgAtoms, svgBondStereoLabels, ionicLinks } =
    usePaperGeometry({
      ionicPairs,
      lines,
      atoms,
      renderedLines,
      renderedAtoms,
      bondOrders,
      aromaticBondIds,
      aromaticRingCenters: previewRingCenters,
      stereoBonds,
      hydrogenPreviewDirections,
      paperSize,
      viewportZoom: viewport.zoom,
      showNodeNumbers,
      stereoLabels,
      bondStereoLabels
    });

  const draftLines: DrawingLine[] = activeDrafts
    .filter((d) => d.moved)
    .map((d) => ({ ...d, id: `draft-${d.pointerId}`, createdAt: '' }));
  const previewLines = [...draftLines, ...(objectGestures.ringLines ?? []), ...(objectGestures.chainLines ?? [])];
  const previewAtoms: DrawingAtom[] = previewLines.flatMap((l) =>
    [l.start, l.end].map((position, i) => ({
      id: `${l.id}-atom-${i}`,
      position,
      atomicNumber: 6,
      formalCharge: 0,
      implicitHydrogenCount: 0,
      hasOpenValence: false
    }))
  );
  const previewOrders: Record<string, BondOrder> = { ...bondOrders };
  const previewStereo: Record<string, BondStereoValue> = { ...stereoBonds };
  for (const l of draftLines) {
    previewOrders[l.id] = armedBondOrder;
    if (armedBondTool.kind === 'wedge')
      previewStereo[l.id] = { type: 'wedge', style: armedBondTool.style, fromNodeId: `${l.id}-atom-0` };
    if (armedBondTool.kind === 'crossed-double') previewStereo[l.id] = { type: 'crossed-double' };
    if (armedBondTool.kind === 'dative')
      previewStereo[l.id] = { type: 'dative', donorNodeId: `${l.id}-atom-0`, acceptorNodeId: `${l.id}-atom-1` };
    if (armedBondTool.kind === 'hydrogen') previewStereo[l.id] = { type: 'hydrogen' };
    if (armedBondTool.kind === 'agostic') previewStereo[l.id] = { type: 'agostic' };
    if (armedBondTool.kind === 'wavy') previewStereo[l.id] = { type: 'wavy', fromNodeId: `${l.id}-atom-0` };
  }
  objectGestures.ringLines?.forEach((l, i) => {
    previewOrders[l.id] = ringTemplate?.aromatic
      ? i % 2 === 0
        ? 2
        : 1
      : ringTemplate?.doubleBondEdges?.includes(i)
        ? 2
        : 1;
  });
  const allPreviewLines = previewLines.length ? [...renderedLines, ...previewLines] : [];
  const allPreviewAtoms = previewLines.length ? [...renderedAtoms, ...previewAtoms] : [];
  const previewGeometry = usePaperGeometry({
    lines: allPreviewLines,
    atoms: allPreviewAtoms,
    renderedLines: allPreviewLines,
    renderedAtoms: allPreviewAtoms,
    bondOrders: previewOrders,
    stereoBonds: previewStereo,
    aromaticBondIds,
    aromaticRingCenters: previewRingCenters,
    hydrogenPreviewDirections,
    paperSize,
    viewportZoom: viewport.zoom
  });
  const geometryFor = (source: readonly DrawingLine[]) => ({
    svgLines: previewGeometry.svgLines.filter((segment) => source.some((l) => segment.id.startsWith(`${l.id}-`))),
    stereoWedges: previewGeometry.stereoWedges.filter((w) => source.some((l) => l.id === w.id)),
    bondStrokeWidth: previewGeometry.bondStrokeWidth
  });

  const ringPreviewLines = useMemo(
    () =>
      !ringTemplate || !ringPointer
        ? []
        : createRingTemplatePlacement(
            ringPointer,
            ringTemplate,
            bondConfig.lengthPx,
            paperSize,
            atoms,
            renderedLines
          ).lines.map((candidate) => ({
            id: candidate.id,
            start: normalizedToPixel(candidate.start, paperSize),
            end: normalizedToPixel(candidate.end, paperSize)
          })),
    [atoms, bondConfig.lengthPx, renderedLines, ringPointer, ringTemplate, paperSize]
  );

  const chainPreviewLines = useMemo(
    () =>
      (objectGestures.chainLines ?? []).map((candidate) => ({
        id: candidate.id,
        start: normalizedToPixel(candidate.start, paperSize),
        end: normalizedToPixel(candidate.end, paperSize)
      })),
    [objectGestures.chainLines, paperSize]
  );

  const shapePreview = useMemo(() => {
    if (!shapeDrag) return null;
    const start = normalizedToPixel(shapeDrag.start, paperSize);
    const current = normalizedToPixel(shapeDrag.current, paperSize);
    return {
      minX: Math.min(start.x, current.x),
      minY: Math.min(start.y, current.y),
      maxX: Math.max(start.x, current.x),
      maxY: Math.max(start.y, current.y)
    };
  }, [shapeDrag, paperSize]);

  // #578: live drag previews for the rectangular marquee and the freehand lasso, so the user can
  // see what they're about to select before releasing the pointer (both were previously
  // invisible during the drag -- marqueeDrag was tracked but never rendered).
  const marqueePreview = useMemo(() => {
    if (!marqueeDrag) return null;
    const start = normalizedToPixel(marqueeDrag.start, paperSize);
    const current = normalizedToPixel(marqueeDrag.current, paperSize);
    return {
      x: Math.min(start.x, current.x),
      y: Math.min(start.y, current.y),
      width: Math.abs(current.x - start.x),
      height: Math.abs(current.y - start.y)
    };
  }, [marqueeDrag, paperSize]);

  const lassoPreview = useMemo(
    () =>
      lassoDrag && lassoDrag.points.length > 1
        ? lassoDrag.points.map((point) => normalizedToPixel(point, paperSize))
        : null,
    [lassoDrag, paperSize]
  );

  const svgDrafts = useMemo(
    () =>
      activeDrafts.map((line) => ({
        ...line,
        x1: roundToOneDecimal(line.start.x * paperSize.width),
        y1: roundToOneDecimal(line.start.y * paperSize.height),
        x2: roundToOneDecimal(line.end.x * paperSize.width),
        y2: roundToOneDecimal(line.end.y * paperSize.height)
      })),
    [activeDrafts, paperSize]
  );

  const proposalMarkers = useMemo(
    () =>
      activeDrafts.flatMap((draft) => {
        if (!draft.moved) return [];
        const pointer = normalizedToPixel(draft.pointerCurrent, paperSize);
        const snapDistance = usesTouchTarget(draft.pointerType)
          ? bondConfig.touchTargetRadiusPx
          : bondConfig.proposalSnapDistancePx;
        return proposedBondEndpoints(normalizedToPixel(draft.start, paperSize), bondConfig).map(
          (point, index): ProposalMarker => {
            const distance = distanceBetween(pointer, point);
            return {
              ...point,
              key: `${draft.pointerId}-${index}`,
              state:
                distance <= snapDistance ? 'snap' : distance <= bondConfig.proposalHotDistancePx ? 'hot' : 'available'
            };
          }
        );
      }),
    [activeDrafts, bondConfig, paperSize]
  );

  const offeredExistingEndpoints = useMemo(
    () =>
      activeDrafts.flatMap((draft) => {
        const start = normalizedToPixel(draft.start, paperSize);
        const offered = nearestPoint(
          normalizedToPixel(draft.pointerCurrent, paperSize),
          uniqueEndpoints(lines, atoms, paperSize).filter((point) => distanceBetween(point, start) >= 0.5),
          usesTouchTarget(draft.pointerType)
            ? bondConfig.touchOfferDistancePx
            : bondConfig.existingEndpointOfferDistancePx
        );
        return offered ? [{ ...offered, pointerId: draft.pointerId }] : [];
      }),
    [activeDrafts, bondConfig.existingEndpointOfferDistancePx, bondConfig.touchOfferDistancePx, atoms, lines, paperSize]
  );

  const arrowGestures = usePaperArrows({
    snapPoint,
    tool,
    annotations: objectGestures.annotations,
    atoms: renderedAtoms,
    lines: renderedLines,
    selection,
    onSelectionChange,
    onArrowChange,
    sizeRef,
    viewportRef,
    ...(documentId ? { documentId } : {})
  });

  // #762 -- cancels every unfinished pointer gesture (bond/chain/ring/shape drafts, drags, pans),
  // keeping completed edits: a second finger starting navigation, or the window losing focus.
  const cancelUnfinishedGestures = useCallback(() => {
    cancelPointerGesture();
    objectGestures.cancel();
    arrowGestures.cancel();
    endPan();
  }, [arrowGestures, cancelPointerGesture, endPan, objectGestures]);
  // #762 -- Pen-only mode: offered once a pen has been used on this device; both facts are
  // remembered per device (best-effort storage: private windows may refuse it).
  const [penSeen, setPenSeen] = useState(() => readDeviceFlag(PEN_SEEN_KEY));
  const [penOnly, setPenOnly] = useState(() => readDeviceFlag(PEN_ONLY_KEY));
  const togglePenOnly = useCallback(() => {
    setPenOnly((current) => {
      writeDeviceFlag(PEN_ONLY_KEY, !current);
      return !current;
    });
  }, []);
  const notePen = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'pen') return;
    if (!penSeen) {
      setPenSeen(true);
      writeDeviceFlag(PEN_SEEN_KEY, true);
    }
  };
  const touchNavigation = usePaperTouchNavigation({
    viewportRef,
    setViewport,
    onCancelEdits: cancelUnfinishedGestures,
    penOnly: penSeen && penOnly
  });
  const cancelAllRef = useRef(() => {});
  useEffect(() => {
    cancelAllRef.current = () => {
      cancelUnfinishedGestures();
      touchNavigation.reset();
    };
  });
  useEffect(() => {
    const cancelAll = () => cancelAllRef.current();
    const hidden = () => {
      if (document.visibilityState === 'hidden') cancelAll();
    };
    window.addEventListener('blur', cancelAll);
    document.addEventListener('visibilitychange', hidden);
    return () => {
      window.removeEventListener('blur', cancelAll);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, []);

  // #762 -- wheel and trackpad: scrolling pans (Shift scrolls sideways); Ctrl/Cmd+wheel and a
  // trackpad pinch (which browsers report as Ctrl+wheel) zoom around the pointer. Native and
  // non-passive, so the browser's own page zoom/scroll is prevented (React's onWheel is passive).
  useEffect(() => {
    const paper = paperRef.current;
    if (!paper) return;
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = paper.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        zoomBy(wheelZoomFactor(event.deltaY, event.deltaMode), {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top
        });
        return;
      }
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1;
      let dx = event.deltaX * unit;
      let dy = event.deltaY * unit;
      if (event.shiftKey && dx === 0) [dx, dy] = [dy, 0];
      setViewport((current) => panViewportBy(current, -dx, -dy));
    };
    paper.addEventListener('wheel', wheel, { passive: false });
    return () => paper.removeEventListener('wheel', wheel);
  }, [setViewport, zoomBy]);

  // #577 -- click-to-place custom rotation pivot. Active only when the toggle is on and the
  // selection has enough atoms to rotate (rotating a single point around itself is meaningless).
  // Uses `atoms`, not `renderedAtoms`, matching what transformSelection itself rotates around.
  const rotationPivotActive = showCenterOfRotation && selectedTransformAtomIds.length > 1;
  const rotationPivotAtoms = rotationPivotActive
    ? atoms.filter((atom) => selectedTransformAtomIds.includes(atom.id))
    : [];
  const rotationPivotCentroid: NormalizedPoint | null =
    rotationPivotAtoms.length > 0
      ? {
          x: rotationPivotAtoms.reduce((sum, atom) => sum + atom.position.x, 0) / rotationPivotAtoms.length,
          y: rotationPivotAtoms.reduce((sum, atom) => sum + atom.position.y, 0) / rotationPivotAtoms.length
        }
      : null;
  const rotationPivotPoint = rotationPivotActive ? (rotationPivot ?? rotationPivotCentroid) : null;
  const rotationPivotMarker = rotationPivotPoint ? normalizedToPixel(rotationPivotPoint, paperSize) : null;

  // #756 -- the text tool: a press on an existing text annotation edits it, anywhere else starts a
  // new one with its top-left corner at the press.
  // #772 -- Fit page / Fit drawing / Fit selection. Points are paper-normalized; the page's own
  // drawing units are surface pixels (1:1 projection). Drawing extents cover atoms and every
  // annotation's complete geometry (text boxes, arrowheads, shapes); fitViewport's padding leaves
  // room for atom labels.
  const toNormalized = (x: number, y: number) => ({ x: x / paperSize.width, y: y / paperSize.height });
  // #805: the page as grown (livePageRect is computed further down, before any click can run this).
  const fitPage = () =>
    markFit(
      fitPoints([
        toNormalized(livePageRect.x, livePageRect.y),
        toNormalized(livePageRect.x + livePageRect.width, livePageRect.y + livePageRect.height)
      ])
    );
  const drawingExtentPoints = (only?: EditorSelection) => {
    const atomIds = only ? new Set([...only.atomIds, ...only.bondIds.flatMap((id) => graphEndpoints[id] ?? [])]) : null;
    const annotationIds = only ? new Set(only.annotationIds ?? []) : null;
    return [
      ...renderedAtoms.filter((atom) => !atomIds || atomIds.has(atom.id)).map((atom) => atom.position),
      ...annotations
        .filter((annotation) => !annotationIds || annotationIds.has(annotation.id))
        .flatMap((annotation) => annotationExtentPoints(annotation, paperSize, canvasTextMeasure))
    ];
  };

  // By position, not event target: select-mode presses capture the pointer on the paper, which
  // retargets the click/dblclick that follows.
  const editableText = (
    annotation: RenderableAnnotation | undefined
  ): Extract<RenderableAnnotation, { kind: 'text' }> | undefined => {
    if (annotation?.kind === 'text') return annotation;
    if (annotation?.kind !== 'shape' || !annotation.callout || !annotation.points[0]) return undefined;
    return {
      id: annotation.id,
      kind: 'text',
      position: annotation.points[0],
      paragraphs: annotation.callout.paragraphs,
      ...(annotation.color ? { color: annotation.color } : {})
    };
  };
  const textAnnotationAt = (clientX: number, clientY: number) => {
    const hit = paperRef.current?.ownerDocument
      .elementsFromPoint(clientX, clientY)
      .find((element) => element.classList.contains('text-annotation-hit') || element.closest('.callout-bubble'));
    const id =
      hit?.closest('g.text-annotation[data-annotation-id]')?.getAttribute('data-annotation-id') ??
      hit?.closest('.callout-bubble')?.getAttribute('data-callout-id');
    const found = id ? annotations.find((annotation) => annotation.id === id) : undefined;
    return editableText(found);
  };
  const startTextEdit = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const existing = textAnnotationAt(event.clientX, event.clientY);
    if (existing) {
      setTextEdit({ id: existing.id, position: existing.position });
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setTextEdit({
      id: globalThis.crypto?.randomUUID?.() ?? `text-${Date.now()}`,
      position: snapPoint(
        screenToNormalized(
          { x: event.clientX - rect.left, y: event.clientY - rect.top },
          viewportRef.current,
          sizeRef.current
        ),
        event
      )
    });
  };
  const editedBubble = textEdit
    ? annotations.find(
        (annotation) => annotation.id === textEdit.id && annotation.kind === 'shape' && annotation.callout
      )
    : undefined;
  const editedText = textEdit
    ? editableText(annotations.find((annotation) => annotation.id === textEdit.id))
    : undefined;
  useEffect(() => {
    const startTyping = (event: KeyboardEvent) => {
      if (
        textEditRef.current ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.isComposing ||
        (event.key.length !== 1 && event.key !== 'Enter')
      )
        return;
      const target = event.target;
      if (target instanceof Element && target.closest('input, textarea, button, [contenteditable="true"]')) return;
      const selected = selection.annotationIds ?? [];
      if (selected.length !== 1 || selection.atomIds.length || selection.bondIds.length) return;
      const annotation = annotations.find((item) => item.id === selected[0]);
      if (annotation?.kind !== 'shape' || !annotation.callout || !annotation.points[0]) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setTextEdit({
        id: annotation.id,
        position: annotation.points[0],
        ...(event.key !== 'Enter' ? { initialInput: event.key } : {})
      });
    };
    document.addEventListener('keydown', startTyping, true);
    return () => document.removeEventListener('keydown', startTyping, true);
  }, [annotations, selection]);
  const commitText = useCallback(
    (paragraphs: readonly TextParagraph[]) => {
      const session = textEditRef.current;
      if (session) onTextCommit?.(session.id, session.position, paragraphs);
    },
    [onTextCommit]
  );

  const handleRotationPivotPlace = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const normalizedPoint = screenToNormalized(
      { x: event.clientX - rect.left, y: event.clientY - rect.top },
      viewportRef.current,
      sizeRef.current
    );
    // Snap onto an atom under the click so "click on a specific atom" pivots exactly on it,
    // rather than a hair off from imprecise pointer placement.
    const pixelPoint = normalizedToPixel(normalizedPoint, sizeRef.current);
    const hit = selectionAtPoint(
      pixelPoint,
      atoms,
      lines,
      sizeRef.current,
      usesTouchTarget(event.pointerType) ? TOUCH_SELECTION_DISTANCE_PX : MOUSE_SELECTION_DISTANCE_PX
    );
    const hitAtom = hit?.kind === 'atom' ? atoms.find((atom) => atom.id === hit.id) : undefined;
    onRotationPivotChange?.(hitAtom ? hitAtom.position : normalizedPoint);
  };

  const clientToPaper = useCallback<ClientToPaperPoint>(
    (x, y) => {
      const paper = paperRef.current;
      if (!paper) return null;
      const rect = paper.getBoundingClientRect();
      // Drops and paste-at land on the grid when Snap to grid is on.
      return snapPoint(
        {
          x: (x - rect.left - paper.clientLeft - viewport.panX) / viewport.zoom / paperSize.width,
          y: (y - rect.top - paper.clientTop - viewport.panY) / viewport.zoom / paperSize.height
        },
        { quiet: true }
      );
    },
    [paperSize.height, paperSize.width, snapPoint, viewport.panX, viewport.panY, viewport.zoom]
  );
  useImperativeHandle(clientPointRef, () => clientToPaper, [clientToPaper]);

  const openContextMenu = (x: number, y: number) => {
    const point = clientToPaper(x, y);
    if (!point || !onPasteAt) return;
    setContextMenu({ x, y, point });
  };

  const style = {
    // #1107: unstyled artifacts use the paper's default black ink; explicit colors
    // remain document choices, independent of the platform theme.
    ...(bondConfig.color ? { '--line-color': bondConfig.color } : {}),
    '--proposal-radius': `${bondConfig.proposalRadiusPx}px`
  } as CSSProperties;

  // #637 -- a selection moves as a whole: while its atoms (or a text block) are dragged, every
  // selected annotation previews at the same offset.
  const selectionOffset = (() => {
    if (objectGestures.annotationOffset) return objectGestures.annotationOffset;
    if (!previewPositions) return undefined;
    const atom = atoms.find((candidate) => previewPositions[candidate.id]);
    const moved = atom && previewPositions[atom.id];
    return atom && moved ? { x: moved.x - atom.position.x, y: moved.y - atom.position.y } : undefined;
  })();
  const selectedAnnotationIds = selection.annotationIds ?? [];
  const selectionPreviewAnnotations = arrowGestures.renderedAnnotations.map((annotation) => {
    if (annotation.kind === 'shape' && annotation.callout?.attachment) {
      const target = annotation.callout.attachment;
      let dx = 0,
        dy = 0;
      if (target.kind === 'annotation') {
        const from = annotations.find((item) => item.id === target.annotationId);
        const to = arrowGestures.renderedAnnotations.find((item) => item.id === target.annotationId);
        if (from?.kind === 'shape' && to?.kind === 'shape' && from.points[0] && to.points[0]) {
          dx =
            to.points.reduce((sum, p) => sum + p.x / to.points.length, 0) -
            from.points.reduce((sum, p) => sum + p.x / from.points.length, 0);
          dy =
            to.points.reduce((sum, p) => sum + p.y / to.points.length, 0) -
            from.points.reduce((sum, p) => sum + p.y / from.points.length, 0);
        }
      } else {
        const ids =
          target.kind === 'atom'
            ? [target.atomId]
            : target.kind === 'atom-set'
              ? target.atomIds
              : (graphEndpoints[target.bondId] ?? []);
        for (const id of ids) {
          const from = atoms.find((atom) => atom.id === id)?.position,
            to = previewPositions?.[id];
          if (from && to) {
            dx += (to.x - from.x) / ids.length;
            dy += (to.y - from.y) / ids.length;
          }
        }
      }
      const bodyOffset =
        selectedAnnotationIds.includes(annotation.id) && selectionOffset ? selectionOffset : { x: dx, y: dy };
      const moved = translateRenderable(annotation, bodyOffset.x, bodyOffset.y);
      if (moved.kind === 'shape')
        return {
          ...moved,
          callout: {
            ...annotation.callout,
            tip: { x: annotation.callout.tip.x + dx, y: annotation.callout.tip.y + dy }
          }
        };
    }
    if (selectionOffset && selectedAnnotationIds.includes(annotation.id)) {
      return translateRenderable(annotation, selectionOffset.x, selectionOffset.y);
    }
    if (annotation.kind !== 'text' || !annotation.calloutTarget) return annotation;
    const target = annotation.calloutTarget;
    if (target.annotationId) {
      const before = annotations.find((item) => item.id === target.annotationId);
      const after = arrowGestures.renderedAnnotations.find((item) => item.id === target.annotationId);
      if (before?.kind === 'shape' && after?.kind === 'shape' && before.points[0] && after.points[0]) {
        const dx = after.points[0].x - before.points[0].x;
        const dy = after.points[0].y - before.points[0].y;
        if (dx || dy) return translateRenderable(annotation, dx, dy);
      }
    }
    if (target.annotationId && selectionOffset && selectedAnnotationIds.includes(target.annotationId)) {
      return translateRenderable(annotation, selectionOffset.x, selectionOffset.y);
    }
    const ids = target.atomIds ?? [];
    if (!previewPositions || !ids.length) return annotation;
    const delta = ids.reduce(
      (offset, id) => {
        const from = atoms.find((atom) => atom.id === id)?.position;
        const to = previewPositions[id];
        return from && to
          ? { x: offset.x + (to.x - from.x) / ids.length, y: offset.y + (to.y - from.y) / ids.length }
          : offset;
      },
      { x: 0, y: 0 }
    );
    return translateRenderable(annotation, delta.x, delta.y);
  });
  // #805 -- the page grows with the content live, while dragging too: recomputed from what is on
  // screen (preview positions included). The stored page carries the committed extent.
  const livePage = useMemo(() => {
    const extent = pageExtentFor(
      page,
      contentBounds(
        // Committed atoms where they are shown, plus bonds, rings and chains still being drawn.
        [...renderedAtoms, ...previewAtoms].map((atom) => ({
          x: atom.position.x * paperSize.width,
          y: atom.position.y * paperSize.height
        })),
        selectionPreviewAnnotations.flatMap((annotation) =>
          annotationExtentPoints(annotation, paperSize, canvasTextMeasure).map((point) => ({
            x: point.x * paperSize.width,
            y: point.y * paperSize.height
          }))
        )
      )
    );
    const current = extentOf(page);
    return extent.left === current.left &&
      extent.top === current.top &&
      extent.columns === current.columns &&
      extent.rows === current.rows
      ? page
      : withExtent(page, extent);
  }, [page, renderedAtoms, previewAtoms, selectionPreviewAnnotations, paperSize]);
  const livePageRect = pageRect(livePage);

  // #838 -- the pointer says what a press will grab (Select tool, mouse and pen): the pointing
  // finger on an atom or bond, an open hand inside the selection box (a closed hand while moving
  // it), resize/rotate shapes on those handles, and the plain arrow on empty paper.
  const [pointerHint, setPointerHint] = useState<string | null>(null);
  const updatePointerHint = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch') return;
    const next = objectGestures.hasActiveGesture()
      ? objectGestures.activeMode() === 'move'
        ? 'grabbing'
        : pointerHint
      : event.buttons === 0
        ? objectGestures.hoverTarget(event)
        : pointerHint;
    if (next !== pointerHint) setPointerHint(next);
  };

  // #807 D6 -- zoom and fit from the keyboard, as the zoom menu lists them: ⌘/Ctrl+= (or +) zoom in,
  // ⌘/Ctrl+- zoom out, ⌘/Ctrl+0 actual size, ⇧1 fit drawing, ⇧2 fit selection. Not while typing
  // (text fields, the rich-text editor, IME composition) or inside an open dialog. The browser's
  // own page zoom stays on its menu and on ⌘/Ctrl+wheel outside the paper.
  const zoomKeysRef = useRef({
    zoomIn: () => {},
    zoomOut: () => {},
    actual: () => {},
    fitDrawing: () => {},
    fitSelection: () => {}
  });
  useEffect(() => {
    zoomKeysRef.current = {
      zoomIn: () => zoomBy(1.25),
      zoomOut: () => zoomBy(1 / 1.25),
      actual: () => actualSize(livePageRect),
      fitDrawing: () => {
        if (atoms.length > 0 || annotations.length > 0) markFit(fitPoints(drawingExtentPoints()));
      },
      fitSelection: () => {
        if (hasSelection(selection)) markFit(fitPoints(drawingExtentPoints(selection)));
      }
    };
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        event.defaultPrevented ||
        event.isComposing ||
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable) ||
        (target instanceof Element && target.closest('[role="dialog"], [aria-modal="true"]'))
      )
        return;
      const keys = zoomKeysRef.current;
      let command: (() => void) | null = null;
      if (isPrimaryModifier(event) && !event.altKey) {
        if (event.key === '=' || event.key === '+') command = keys.zoomIn;
        else if (event.key === '-' || event.key === '_') command = keys.zoomOut;
        else if (event.key === '0') command = keys.actual;
      } else if (event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey) {
        // The physical key, so ⇧1/⇧2 work whatever the keyboard layout prints on it.
        if (event.code === 'Digit1') command = keys.fitDrawing;
        else if (event.code === 'Digit2') command = keys.fitSelection;
      }
      if (!command) return;
      event.preventDefault();
      command();
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, []);
  const liveExtent = extentOf(livePage);

  return (
    <div
      ref={paperRef}
      data-testid="drawing-paper"
      className={`drawing-paper ${tool === 'select' ? 'select-mode' : tool === 'pan' ? 'pan-mode' : tool === 'text' ? 'text-mode' : 'draw-mode'}${rotationPivotActive ? ' rotation-pivot-mode' : ''}`}
      data-pointer-hint={tool === 'select' ? (pointerHint ?? undefined) : undefined}
      title={(hoveredAtomId && markReasons?.get(hoveredAtomId)) || undefined}
      style={style}
      role="application"
      // The drawing application is keyboard focusable for its canvas context menu.
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-haspopup={onPasteAt ? 'menu' : undefined}
      onKeyDown={(event) => {
        if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          openContextMenu(rect.left + rect.width / 2, rect.top + rect.height / 2);
        }
      }}
      aria-label={`Chemical bond drawing paper. View zoom ${Math.round(viewport.zoom * 100)} percent.`}
      onPointerEnter={trackPastePosition}
      onPointerDown={(event) => {
        if (event.button === 2) return;
        notePen(event);
        if (event.pointerType === 'pen') touchNavigation.penDown();
        // #762 -- a second finger navigates (and cancels the unfinished edit) instead of drawing;
        // in Pen-only mode every finger navigates.
        if (touchNavigation.pointerDown(event)) return;
        // A press outside an open text editor only ends the edit (the editor commits on it).
        if (textEditRef.current || textClosingPressRef.current === event.nativeEvent.timeStamp) return;
        if (
          tool.startsWith('callout-') ||
          selection.annotationIds?.some((id) => annotations.some((a) => a.id === id && a.kind === 'shape' && a.callout))
        )
          event.currentTarget.focus({ preventScroll: true });
        trackPastePosition(event);
        if (tool === 'text' && onTextCommit && event.button === 0) {
          startTextEdit(event);
          return;
        }
        if (rotationPivotActive) {
          handleRotationPivotPlace(event);
          return;
        }
        if (objectGestures.hasActiveGesture()) {
          event.preventDefault();
          return;
        }
        if (!arrowGestures.pointerDown(event) && !objectGestures.pointerDown(event)) handlePointerDown(event);
        updatePointerHint(event);
      }}
      onPointerMove={(event) => {
        if (touchNavigation.pointerMove(event)) return;
        trackPastePosition(event);
        // The text tool has no hover previews (bond proposals belong to the drawing tools).
        if (tool === 'text' && !panDrag) return;
        if (!objectGestures.pointerMove(event) && !arrowGestures.pointerMove(event)) handlePointerMove(event);
        updatePointerHint(event);
      }}
      onPointerLeave={() => {
        if (pointerHint !== null) setPointerHint(null);
        handlePointerLeave();
      }}
      onPointerUp={(event) => {
        if (event.pointerType === 'pen') touchNavigation.penUp();
        // The release itself may snap (a bond's final end), so the feedback clears afterwards.
        if (!touchNavigation.pointerEnd(event)) {
          trackPastePosition(event);
          if (!objectGestures.finish(event, false) && !arrowGestures.finish(event, false)) finishPointer(event, false);
        }
        showSnapFeedback(null);
        updatePointerHint(event);
      }}
      onPointerCancel={(event) => {
        if (event.pointerType === 'pen') touchNavigation.penUp();
        if (
          !touchNavigation.pointerEnd(event) &&
          !objectGestures.finish(event, true) &&
          !arrowGestures.finish(event, true)
        )
          finishPointer(event, true);
        showSnapFeedback(null);
      }}
      onLostPointerCapture={(event) => {
        if (touchNavigation.isNavigating()) return;
        if (objectGestures.finish(event, true) || arrowGestures.finish(event, true)) return;
        finishPointer(event, true);
      }}
      onDoubleClick={(event) => {
        if (objectGestures.doubleClick(event)) return;
        if (!onTextCommit) return;
        const existing = textAnnotationAt(event.clientX, event.clientY);
        if (existing) setTextEdit({ id: existing.id, position: existing.position });
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        openContextMenu(event.clientX, event.clientY);
      }}
    >
      {contextMenu ? (
        <PaperContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          canPaste={canPaste}
          onPaste={() => onPasteAt?.(contextMenu.point)}
          onClose={closeContextMenu}
        />
      ) : null}
      {/* #772 -- the page sheet, behind the grid and every drawing layer; content may overflow it. */}
      <div
        className="paper-page-sheet"
        aria-hidden="true"
        data-testid="page-sheet"
        data-columns={liveExtent.columns}
        data-rows={liveExtent.rows}
        style={{
          left: viewport.panX + livePageRect.x * viewport.zoom,
          top: viewport.panY + livePageRect.y * viewport.zoom,
          width: livePageRect.width * viewport.zoom,
          height: livePageRect.height * viewport.zoom,
          // #805: faint seams between the base pages the page has grown to.
          ...(liveExtent.columns > 1 || liveExtent.rows > 1
            ? {
                backgroundImage:
                  'linear-gradient(to right, var(--page-seam) 1px, transparent 1px), linear-gradient(to bottom, var(--page-seam) 1px, transparent 1px)',
                backgroundSize: `${page.width * viewport.zoom}px ${page.height * viewport.zoom}px`,
                backgroundPosition: '-1px -1px'
              }
            : {})
        }}
      />
      <canvas
        ref={gridCanvasRef}
        className="paper-layer grid-layer"
        aria-hidden="true"
        data-grid={grid ? grid.kind : 'hidden'}
      />
      <PaperBackgroundLayer annotations={selectionPreviewAnnotations} paperSize={paperSize} viewport={viewport} />
      <canvas ref={webGlCanvasRef} className="paper-layer webgl-layer" aria-hidden="true" />
      {snapFeedback ? (
        // #772 part 3 -- the active snap target: guide lines across the paper and a target marker.
        <svg className="paper-layer snap-feedback" aria-hidden="true" data-testid="snap-feedback">
          {snapFeedback.guideX !== undefined ? (
            <line
              className="snap-guide"
              data-axis="x"
              x1={viewport.panX + snapFeedback.guideX * viewport.zoom}
              x2={viewport.panX + snapFeedback.guideX * viewport.zoom}
              y1={0}
              y2="100%"
            />
          ) : null}
          {snapFeedback.guideY !== undefined ? (
            <line
              className="snap-guide"
              data-axis="y"
              x1={0}
              x2="100%"
              y1={viewport.panY + snapFeedback.guideY * viewport.zoom}
              y2={viewport.panY + snapFeedback.guideY * viewport.zoom}
            />
          ) : null}
          <circle
            className="snap-target"
            cx={viewport.panX + snapFeedback.x * viewport.zoom}
            cy={viewport.panY + snapFeedback.y * viewport.zoom}
            r={5}
          />
        </svg>
      ) : null}
      {(() => {
        const viewportControls = (
          <PaperViewportControls
            zoomPercent={Math.round(viewport.zoom * 100)}
            fitted={fitZoom !== null && Math.abs(viewport.zoom - fitZoom) < 1e-9}
            onZoomIn={() => zoomBy(1.25)}
            onZoomOut={() => zoomBy(1 / 1.25)}
            onZoomTo={(percent) => zoomBy(percent / 100 / viewport.zoom)}
            onFitPage={fitPage}
            onFitDrawing={() => markFit(fitPoints(drawingExtentPoints()))}
            fitDrawingDisabled={atoms.length === 0 && annotations.length === 0}
            onFitSelection={() => markFit(fitPoints(drawingExtentPoints(selection)))}
            fitSelectionDisabled={!hasSelection(selection)}
            onActualSize={() => actualSize(livePageRect)}
            {...(onPageChange ? { page: { value: livePage, onChange: onPageChange } } : {})}
            grid={{
              visible: showGrid,
              onToggle: toggleGrid,
              kind: gridKind,
              onKindChange: changeGridKind,
              snap: snapGrid,
              onSnapToggle: toggleSnapGrid,
              guides: smartGuides,
              onGuidesToggle: toggleSmartGuides,
              bypassHeld,
              onBypassHold: holdBypass
            }}
            {...(penSeen ? { penOnly: { active: penOnly, onToggle: togglePenOnly } } : {})}
            {...(viewHiddenGroups ? { hiddenGroups: viewHiddenGroups } : {})}
            overflowContainer={viewOverflowContainer}
            overflowAnchor={viewOverflowAnchor}
            {...(onViewOverflowClose ? { onOverflowClose: onViewOverflowClose } : {})}
          />
        );
        return viewportControlsContainer ? createPortal(viewportControls, viewportControlsContainer) : viewportControls;
      })()}
      <PaperVectorLayer
        ionicLinks={ionicLinks}
        shownIonicIds={shownIonicIds}
        haptoLabels={haptoLabels}
        paperSize={paperSize}
        atomAppearance={atomAppearance}
        bondAppearance={bondAppearance}
        cycleFills={cycleFills}
        {...(markushAttachmentLines?.length ? { markushAttachmentLines } : {})}
        {...(reactionZones?.length ? { reactionZones: previewZones(reactionZones, atomDrag, paperSize) } : {})}
        reactionZoneRefs={reactionZoneRefs}
        {...(repeatBrackets.length ? { repeatUnitBrackets: repeatBrackets } : {})}
        viewport={viewport}
        labelScale={labelScale}
        bondStrokeWidth={bondStrokeWidth}
        svgLines={svgLines}
        stereoWedges={stereoWedges}
        ringPreviewLines={
          objectGestures.ringLines?.map((l) => ({
            id: l.id,
            start: normalizedToPixel(l.start, paperSize),
            end: normalizedToPixel(l.end, paperSize)
          })) ?? ringPreviewLines
        }
        chainPreviewLines={chainPreviewLines}
        selection={selection}
        renderedLines={renderedLines}
        renderedAtoms={renderedAtoms}
        svgAtoms={svgAtoms}
        bondStereoLabels={svgBondStereoLabels}
        svgDrafts={svgDrafts}
        proposalMarkers={proposalMarkers}
        hoveredEndpoint={hoveredEndpoint}
        offeredExistingEndpoints={offeredExistingEndpoints}
        blinkMarkers={blinkMarkers}
        invalidAtomIds={invalidAtomIds}
        invalidBondIds={invalidBondIds}
        {...(warningAtomIds ? { warningAtomIds } : {})}
        {...(warningBondIds ? { warningBondIds } : {})}
        proposalRadiusPx={bondConfig.proposalRadiusPx}
        annotations={
          textEdit
            ? selectionPreviewAnnotations
                .filter((annotation) => annotation.id !== textEdit.id || annotation.kind === 'shape')
                .map((annotation) =>
                  annotation.id === textEdit.id && annotation.kind === 'shape' && annotation.callout
                    ? { ...annotation, callout: { ...annotation.callout, paragraphs: [{ runs: [] }] } }
                    : annotation
                )
            : selectionPreviewAnnotations
        }
        shapePreview={shapePreview}
        arrowSnapTargets={arrowGestures.snapTargets}
        calloutTargets={objectGestures.calloutTargets}
        {...(objectGestures.activeCalloutTarget ? { activeCalloutTarget: objectGestures.activeCalloutTarget } : {})}
        objectHandles={objectGestures.handles}
        draftGeometry={geometryFor(draftLines)}
        showNodeNumbers={showNodeNumbers}
        showStereoLabels={stereoLabels !== undefined}
        showBondAcceptors={showBondAcceptors}
        rotationPivotMarker={rotationPivotMarker}
        marqueePreview={marqueePreview}
        lassoPreview={lassoPreview}
        {...(objectGestures.ringLines ? { ringGeometry: geometryFor(objectGestures.ringLines) } : {})}
        {...(objectGestures.chainLines ? { chainGeometry: geometryFor(objectGestures.chainLines) } : {})}
      />

      {textEdit && onTextCommit
        ? (() => {
            const bubbleTextBounds =
              editedBubble?.kind === 'shape' && editedBubble.callout && editedBubble.points[0] && editedBubble.points[1]
                ? calloutTextBounds(
                    normalizedToPixel(editedBubble.points[0], paperSize),
                    normalizedToPixel(editedBubble.points[1], paperSize),
                    editedBubble.callout.variant
                  )
                : undefined;
            const pixel = bubbleTextBounds?.a ?? normalizedToPixel(textEdit.position, paperSize);
            return (
              <RichTextEditor
                key={textEdit.id}
                committed={editedText?.paragraphs ?? null}
                {...(textEdit.initialInput ? { initialInput: textEdit.initialInput } : {})}
                {...(bubbleTextBounds
                  ? {
                      centeredWidth: (bubbleTextBounds.b.x - bubbleTextBounds.a.x) * viewport.zoom,
                      centeredHeight: (bubbleTextBounds.b.y - bubbleTextBounds.a.y) * viewport.zoom
                    }
                  : {})}
                left={pixel.x * viewport.zoom + viewport.panX}
                top={pixel.y * viewport.zoom + viewport.panY}
                zoom={viewport.zoom}
                boundsWidth={paperSize.width}
                {...(editedText?.color ? { color: editedText.color } : {})}
                label={t('drawingPaper.textEditor')}
                onCommit={commitText}
                onExit={endTextEdit}
                {...(onUndo ? { onUndo } : {})}
                {...(onRedo ? { onRedo } : {})}
                {...(renderTextToolbar ? { renderToolbar: renderTextToolbar } : {})}
              />
            );
          })()
        : null}
      {/* #743 -- not over an arrow or shape either: the paper is no longer empty. */}
      {atoms.length === 0 && annotations.length === 0 && activeDrafts.length === 0 && !textEdit ? (
        <div className="paper-hint">
          {/* #510 -- unlike the two decorative how-to lines below, this is real state feedback
              (search-mode-specific idle copy from the host app), so it isn't aria-hidden. */}
          {idleHint ? <span>{idleHint}</span> : null}
          <span aria-hidden="true">{t('drawingPaper.hint')}</span>
          <span aria-hidden="true">{t('drawingPaper.hintSecondary')}</span>
        </div>
      ) : null}
      {objectGestures.notice ? <output className="object-edit-notice">{objectGestures.notice}</output> : null}
      {objectGestures.chainHud ? (
        <output
          className="chain-drawing-hud"
          style={{
            left: normalizedToPixel(objectGestures.chainHud.point, paperSize).x * viewport.zoom + viewport.panX,
            top: normalizedToPixel(objectGestures.chainHud.point, paperSize).y * viewport.zoom + viewport.panY
          }}
        >
          <span>{roundToOneDecimal(objectGestures.chainHud.angleDegrees)}°</span>
          <span>
            {objectGestures.chainHud.segmentAtomCount} atom{objectGestures.chainHud.segmentAtomCount === 1 ? '' : 's'}
          </span>
          {objectGestures.chainHud.totalAtomCount !== undefined ? (
            <span>Total {objectGestures.chainHud.totalAtomCount}</span>
          ) : null}
        </output>
      ) : null}
      <div className="pointer-count" aria-live="polite">
        {t('drawingPaper.activePointers', { count: activeDrafts.length })}
      </div>
    </div>
  );
}

/** ADR 0014 -- while a reaction box is dragged, draw it shifted by the drag (its molecules already
 *  preview through the atom drag), so box and contents move together on screen. */
function previewZones(
  zones: readonly PaperReactionZone[],
  drag: { readonly reactionZone?: PaperReactionZone['zone']; readonly offset?: NormalizedPoint } | null,
  paperSize: PaperSize
): readonly PaperReactionZone[] {
  if (!drag?.reactionZone || !drag.offset) return zones;
  const dx = drag.offset.x * paperSize.width;
  const dy = drag.offset.y * paperSize.height;
  return zones.map((zone) =>
    zone.zone === drag.reactionZone
      ? {
          ...zone,
          rect: {
            minX: zone.rect.minX + dx,
            minY: zone.rect.minY + dy,
            maxX: zone.rect.maxX + dx,
            maxY: zone.rect.maxY + dy
          }
        }
      : zone
  );
}
